import {
  RouteAuditData,
  RouteAuditSummary,
  Shipment,
  ShipmentIssues
} from './IRouteAudit';

const MAX_RATING = 5;

const SHIPMENT_ISSUES = [
  { label: 'Damaged', key: 'damagedShipmentsCount' },
  { label: 'Shortage', key: 'shortageShipmentsCount' },
  { label: 'Out of Sequence', key: 'outOfSequenceShipmentsCount' }
];

const getShipmentIssues = (shipments: Shipment[] = []): ShipmentIssues => {
  return shipments.reduce(
    (acc, { damagedPieces, shortagePieces, isOutOfSequence }) => {
      const hasDamage = damagedPieces > 0;
      const hasShortage = shortagePieces > 0;

      if (hasDamage) acc.damagedShipmentsCount++;
      if (hasShortage) acc.shortageShipmentsCount++;
      if (isOutOfSequence) acc.outOfSequenceShipmentsCount++;
      if (hasDamage || hasShortage || isOutOfSequence) acc.totalShipmentsWithIssuesCount++;
      return acc;
    },
    {
      damagedShipmentsCount: 0,
      shortageShipmentsCount: 0,
      outOfSequenceShipmentsCount: 0,
      totalShipmentsWithIssuesCount: 0
    }
  );
};

const getDataWithShipmentIssues = (data: RouteAuditData): RouteAuditData => {
  return data.map((record) => ({
    ...record,
    overallQualityRating: getRoundedRating(record.overallQualityRating),
    shipmentIssues: getShipmentIssues(record.shipments)
  }));
};

const getShipmentsList = (data: RouteAuditData): Shipment[] => {
  return data.map(record => record.shipments).flat();
};

const getRoundedRating = (rating: number): number => {
  return Math.min(Math.max(Math.round(rating), 0), MAX_RATING);
};

const getSummaryData = (data: RouteAuditData): RouteAuditSummary => {
  const aggregatedSummary = data.reduce(
    (acc, { isTrailerRecouped, shipmentIssues, overallQualityRating }) => {
      if (isTrailerRecouped) acc.totalRecouped++;

      acc.totalDamagedShipments += shipmentIssues?.damagedShipmentsCount || 0;
      acc.totalShortageShipments += shipmentIssues?.shortageShipmentsCount || 0;
      acc.totalOutOfSequenceShipments += shipmentIssues?.outOfSequenceShipmentsCount || 0;
      acc.totalShipmentsWithIssues += shipmentIssues?.totalShipmentsWithIssuesCount || 0;
      acc.totalRating += overallQualityRating || 0;

      return acc;
    },
    {
      totalRecouped: 0,
      totalDamagedShipments: 0,
      totalShortageShipments: 0,
      totalOutOfSequenceShipments: 0,
      totalShipmentsWithIssues: 0,
      totalRating: 0
    }
  );

  const totalAudits = data.length;
  const averageQualityRating =
    totalAudits > 0 ? parseFloat((aggregatedSummary.totalRating / totalAudits).toFixed(1)) : 0;

  return {
    totalAudits,
    totalRecouped: aggregatedSummary.totalRecouped,
    totalDamagedShipments: aggregatedSummary.totalDamagedShipments,
    totalShortageShipments: aggregatedSummary.totalShortageShipments,
    totalOutOfSequenceShipments: aggregatedSummary.totalOutOfSequenceShipments,
    totalShipmentsWithIssues: aggregatedSummary.totalShipmentsWithIssues,
    averageQualityRating
  };
};

const getTerminalOptions = (terminalData: any[]): { label: string; value: string }[] => {
  return terminalData.map((terminal: any) => ({
    label: `${terminal.terminalName} (${terminal.terminalCode})`,
    value: terminal.terminalCode
  }));
};
const getShipmentIssuesString = (shipmentIssues: ShipmentIssues): string => {
  if (!shipmentIssues || shipmentIssues.totalShipmentsWithIssuesCount === 0) {
    return 'None';
  }

  const issuesString = SHIPMENT_ISSUES.filter(
    (issue) => shipmentIssues[issue.key as keyof ShipmentIssues] > 0
  )
    .map((issue) => issue.label)
    .join(', ')
    .trim();

  return issuesString;
};

export {
  getSummaryData,
  getDataWithShipmentIssues,
  getTerminalOptions,
  getShipmentIssuesString,
  getShipmentsList,
  SHIPMENT_ISSUES
};
